<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Header -->
    <HeaderComponent />

    <!-- Hero Banner -->
    <section class="bg-gradient-to-br from-blue-600 to-cyan-600 text-white py-4">
      <div class="container mx-auto px-4 text-center">
        <h1 class="text-2xl font-bold mb-1">{{ themeName }}</h1>
        <p class="text-base text-blue-100 max-w-2xl mx-auto">{{ themeDescription }}</p>
      </div>
    </section>

    <!-- Sub-Section Navigation -->
    <nav v-if="subSections.length" class="bg-white shadow-sm border-b border-slate-200 sticky top-[6%] z-40">
      <div class="container mx-auto px-4">
        <div class="flex flex-wrap justify-center gap-2 py-4">
          <button
            :class="[
              'px-6 py-2 rounded-full font-medium text-sm transition-all duration-200',
              activeSubsection === 'All' ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-600',
            ]"
            @click="handleSubsectionChange('All')"
          >
            All ({{ totalArticles }})
          </button>
          <button
            v-for="(sub, index) in subSections"
            :key="index"
            :class="[
              'px-6 py-2 rounded-full font-medium text-sm transition-all duration-200',
              activeSubsection === sub.name ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-600',
            ]"
            @click="handleSubsectionChange(sub.name)"
          >
            {{ sub.name }} ({{ getSubsectionCount(sub.name) }})
          </button>
        </div>
      </div>
    </nav>

    <!-- Filters and Search -->
    <section class="bg-white border-b border-slate-200">
      <div class="container mx-auto px-4 py-6">
        <div class="flex flex-col gap-4">
          <!-- Search -->
          <div class="relative flex-1 max-w-md mx-auto lg:mx-0">
            <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search articles..."
              class="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <!-- Filters Row -->
          <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <!-- Tag Filter -->
            <div class="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <select
                v-model="selectedTag"
                class="w-full sm:w-auto px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Tags</option>
                <option v-for="tag in availableTags" :key="tag" :value="tag">{{ tag }}</option>
              </select>

              <!-- Sort Options -->
              <select
                v-model="sortBy"
                class="w-full sm:w-auto px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="date">Latest First</option>
                <option value="title">A-Z</option>
                <option value="trending">Trending</option>
              </select>
            </div>

            <!-- Results Info -->
            <div class="text-sm text-slate-600 order-first sm:order-last">
              {{ filteredArticles.length }} article{{ filteredArticles.length !== 1 ? 's' : '' }}
            </div>
          </div>

          <!-- Active Filters -->
          <div v-if="hasActiveFilters" class="flex flex-wrap gap-2">
            <span class="text-sm text-slate-600">Active filters:</span>
            <button
              v-if="searchQuery"
              @click="searchQuery = ''"
              class="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm hover:bg-blue-200 transition-colors"
            >
              Search: "{{ searchQuery }}"
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            <button
              v-if="selectedTag"
              @click="selectedTag = ''"
              class="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm hover:bg-green-200 transition-colors"
            >
              Tag: {{ selectedTag }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            <button @click="clearAllFilters" class="px-3 py-1 text-slate-600 hover:text-slate-800 text-sm underline">
              Clear all
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <main class="py-8">
      <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 xl:grid-cols-8 gap-8">
          <!-- Left Ad Sidebar -->
          <div class="hidden xl:block xl:col-span-1">
            <div class="sticky top-48">
              <div
                class="bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-dashed border-blue-300 rounded-lg p-4 text-center min-h-[600px] w-[120px] flex flex-col justify-center mx-auto"
              >
                <div class="text-blue-600 text-xs font-medium mb-2">Ad Space</div>
                <div class="text-blue-500 text-xs">120x600</div>
              </div>
            </div>
          </div>

          <!-- Main Content Area -->
          <div class="xl:col-span-6">
            <div v-if="activeSubsectionObject && activeSubsection !== 'All'" class="text-center mb-12">
              <h2 class="heading-lg text-slate-900 mb-4">{{ activeSubsectionObject.name }}</h2>
              <p class="text-lg text-slate-600 max-w-3xl mx-auto">{{ activeSubsectionObject.description }}</p>
            </div>

            <!-- Results Summary -->
            <div class="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <p class="text-slate-600">
                Showing {{ filteredArticles.length }} of {{ totalArticles }} articles
                <span v-if="hasActiveFilters" class="text-blue-600">• Filters applied</span>
              </p>
              <div v-if="filteredArticles.length !== totalArticles" class="text-sm text-slate-500">
                {{ totalArticles - filteredArticles.length }} articles filtered out
              </div>
            </div>

            <!-- Subsection Grids -->
            <div v-if="activeSubsection === 'All'" class="space-y-12">
              <div v-for="(subsection, index) in subSections" :key="subsection.name" class="subsection-group">
                <!-- Subsection Header -->
                <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-6 pb-4 border-b border-slate-200">
                  <div>
                    <h2 class="text-2xl font-bold text-slate-900 mb-2">{{ subsection.name }}</h2>
                    <p class="text-slate-600 text-sm">{{ subsection.description }}</p>
                  </div>
                  <div class="mt-3 sm:mt-0 flex items-center gap-4">
                    <span class="text-sm text-slate-500">{{ getSubsectionArticles(subsection.name).length }} articles</span>
                    <button
                      @click="handleSubsectionChange(subsection.name)"
                      class="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                    >
                      View All
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Subsection Articles Grid -->
                <div v-if="getSubsectionArticles(subsection.name).length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  <EnhancedArticleCard
                    v-for="article in getSubsectionArticles(subsection.name).slice(0, 8)"
                    :key="article.id"
                    :article="article"
                    @tag-click="handleTagClick"
                  />
                </div>

                <!-- Ad Placement after first subsection -->
                <div v-if="index === 0" class="my-8">
                  <div class="bg-gradient-to-r from-indigo-100 to-blue-100 border-2 border-dashed border-indigo-300 rounded-lg p-6 text-center">
                    <div class="text-indigo-600 text-sm font-medium mb-2">Advertisement</div>
                    <div class="text-indigo-500 text-xs">728x90 Leaderboard</div>
                  </div>
                </div>

                <!-- Show More Button for Subsection -->
                <div v-if="getSubsectionArticles(subsection.name).length > 8" class="mt-6 text-center">
                  <button
                    @click="handleSubsectionChange(subsection.name)"
                    class="inline-flex items-center gap-2 px-6 py-3 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-medium transition-colors"
                  >
                    Show All {{ getSubsectionArticles(subsection.name).length }} Articles
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                </div>

                <!-- Ad Placement after second subsection -->
                <div v-if="index === 1" class="my-8 flex justify-center">
                  <div
                    class="bg-gradient-to-br from-teal-100 to-cyan-100 border-2 border-dashed border-teal-300 rounded-lg p-6 text-center w-[320px] h-[270px] flex flex-col justify-center"
                  >
                    <div class="text-teal-600 text-sm font-medium mb-2">Advertisement</div>
                    <div class="text-teal-500 text-xs">300x250 Medium Rectangle</div>
                  </div>
                </div>

                <!-- Empty State for Subsection -->
                <div v-if="getSubsectionArticles(subsection.name).length === 0" class="text-center py-12 text-slate-500">
                  <div class="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                    <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      ></path>
                    </svg>
                  </div>
                  <p class="text-sm">No articles found for {{ subsection.name }}</p>
                </div>
              </div>
            </div>

            <!-- Single Subsection View -->
            <div v-else class="single-subsection-view">
              <!-- Back Button -->
              <div class="mb-6">
                <button @click="handleSubsectionChange('All')" class="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                  Back to All Subsections
                </button>
              </div>

              <!-- Single Subsection Header -->
              <div class="mb-6 pb-4 border-b border-slate-200">
                <h2 class="text-2xl font-bold text-slate-900 mb-2">{{ activeSubsectionObject?.name }}</h2>
                <p class="text-slate-600">{{ activeSubsectionObject?.description }}</p>
              </div>

              <!-- Single Subsection Articles Grid -->
              <div v-if="filteredArticles.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <EnhancedArticleCard v-for="article in filteredArticles" :key="article.id" :article="article" @tag-click="handleTagClick" />
              </div>
            </div>

            <!-- Empty State for Single Subsection -->
            <div v-else class="text-center py-12 text-slate-500">
              <div class="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  ></path>
                </svg>
              </div>
              <p class="text-sm">No articles found</p>
              <p class="text-xs text-slate-400 mt-1">Try adjusting your search or filter criteria</p>
            </div>
          </div>

          <!-- Right Ad Sidebar -->
          <div class="hidden xl:block xl:col-span-1">
            <div class="sticky top-48">
              <div
                class="bg-gradient-to-br from-green-100 to-green-200 border-2 border-dashed border-green-300 rounded-lg p-4 text-center min-h-[600px] w-[120px] flex flex-col justify-center mx-auto"
              >
                <div class="text-green-600 text-xs font-medium mb-2">Ad Space</div>
                <div class="text-green-500 text-xs">120x600</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Global Empty State (when no articles match filters in All view) -->
    <div v-if="activeSubsection === 'All' && subSections.every((sub) => getSubsectionArticles(sub.name).length === 0)" class="text-center py-16">
      <div class="bg-white rounded-xl shadow-lg p-12 border border-slate-200 max-w-md mx-auto">
        <svg class="w-16 h-16 mx-auto text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          ></path>
        </svg>
        <h3 class="text-lg font-semibold text-slate-900 mb-2">No Articles Found</h3>
        <p class="text-slate-600">
          {{ hasActiveFilters ? 'Try adjusting your filters or search terms.' : 'Check back soon for new content in this section.' }}
        </p>
      </div>
    </div>
  </div>

  <!-- Footer -->
  <FooterComponent />
</template>

<script setup>
  import { ref, onMounted, watch, computed } from 'vue';
  import { useRoute } from 'vue-router';
  import HeaderComponent from '@/components/Header/Header.vue';
  import FooterComponent from '@/components/Footer/Footer.vue';
  import ArticleCard from '@/components/Common/ArticleCard.vue';
  import EnhancedArticleCard from '@/components/Common/EnhancedArticleCard.vue';
  import { getAllArticles, loadSections } from '@/utils/dataProcessor.js';

  // Access the route for the theme parameter
  const route = useRoute();

  // Reactive states
  const themeName = ref('');
  const themeDescription = ref('');
  const subSections = ref([]);
  const activeSubsection = ref('All');
  const searchQuery = ref('');
  const selectedTag = ref('');
  const sortBy = ref('date');

  // Load or update the theme
  const updateTheme = () => {
    const themeParam = route.params.theme;
    if (themeParam) {
      const sectionsData = loadSections();
      const themeObj = sectionsData.find((item) => item.topNavItem.toLowerCase() === themeParam.toLowerCase());
      if (themeObj) {
        themeName.value = themeObj.topNavItem;
        themeDescription.value = `Explore the latest updates and insights in ${themeObj.topNavItem}.`;
        subSections.value = themeObj.sections;
        // Reset to "All" when theme changes
        activeSubsection.value = 'All';
      } else {
        // Fallback if theme is not found
        themeName.value = 'Unknown Theme';
        themeDescription.value = '';
        subSections.value = [];
        activeSubsection.value = 'All';
      }
    }
    // Reset filters when theme changes
    searchQuery.value = '';
    selectedTag.value = '';
  };

  onMounted(() => {
    updateTheme();
  });

  watch(
    () => route.params.theme,
    () => {
      updateTheme();
    }
  );

  // Computed: all articles for the current theme
  const themeArticles = computed(() => {
    const allArticles = getAllArticles();
    return allArticles.filter((article) => article.section && article.section.toLowerCase() === themeName.value.toLowerCase());
  });

  // Computed: total articles count
  const totalArticles = computed(() => themeArticles.value.length);

  // Computed: the currently active sub-section object
  const activeSubsectionObject = computed(() => {
    return subSections.value.find((sub) => sub.name === activeSubsection.value) || null;
  });

  // Computed: available tags from current theme articles
  const availableTags = computed(() => {
    const tags = new Set();
    themeArticles.value.forEach((article) => {
      if (article.tags && Array.isArray(article.tags)) {
        article.tags.forEach((tag) => tags.add(tag));
      }
    });
    return Array.from(tags).sort();
  });

  // Function to get subsection count
  const getSubsectionCount = (subsectionName) => {
    return themeArticles.value.filter((article) => {
      // For subsection filtering, we'll use a mapping approach
      // This can be enhanced based on how subsections relate to articles
      const subsectionKeywords = getSubsectionKeywords(subsectionName);
      return (
        article.tags?.some((tag) => subsectionKeywords.some((keyword) => tag.toLowerCase().includes(keyword.toLowerCase()))) ||
        article.title.toLowerCase().includes(subsectionName.toLowerCase())
      );
    }).length;
  };

  // Function to get articles for a specific subsection
  const getSubsectionArticles = (subsectionName) => {
    let articles = themeArticles.value;

    // Apply search and tag filters to subsection articles
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase();
      articles = articles.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.description.toLowerCase().includes(query) ||
          article.tags?.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    if (selectedTag.value) {
      articles = articles.filter((article) => article.tags?.includes(selectedTag.value));
    }

    // Filter by subsection keywords
    const subsectionKeywords = getSubsectionKeywords(subsectionName);
    articles = articles.filter((article) => {
      return (
        article.tags?.some((tag) => subsectionKeywords.some((keyword) => tag.toLowerCase().includes(keyword.toLowerCase()))) ||
        article.title.toLowerCase().includes(subsectionName.toLowerCase())
      );
    });

    // Sort articles
    articles = [...articles].sort((a, b) => {
      switch (sortBy.value) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'trending':
          // Sort by trending status, then by date
          if (a.isTrending && !b.isTrending) return -1;
          if (!a.isTrending && b.isTrending) return 1;
          return new Date(b.date) - new Date(a.date);
        case 'date':
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

    return articles;
  };

  // Helper function to map subsections to keywords
  const getSubsectionKeywords = (subsectionName) => {
    const mappings = {
      'Breaking News': ['news', 'breaking', 'announcement', 'unveiled'],
      'Market Trends & Price Analysis': ['market', 'price', 'economic', 'analysis', 'trends'],
      'Tech Industry Analysis': ['industry', 'analysis', 'strategy', 'technology trends'],
      'Hardware Reviews': ['reviews', 'hardware', 'computer hardware'],
      'Gaming Benchmarks': ['benchmarking', 'performance testing', 'gaming'],
      Comparisons: ['comparisons', 'vs', 'comparison'],
      'Build Guides': ['build guides', 'gaming setups', 'tutorial'],
      'Overclocking & Tuning': ['overclocking', 'optimization', 'tuning'],
      'DIY Modding & Customization': ['diy & modding', 'modding', 'customization'],
    };
    return mappings[subsectionName] || [subsectionName.toLowerCase()];
  };

  // Computed: filtered articles based on search, tags, and subsection
  const filteredArticles = computed(() => {
    let articles = themeArticles.value;

    // Filter by subsection if not "All"
    if (activeSubsection.value !== 'All') {
      const subsectionKeywords = getSubsectionKeywords(activeSubsection.value);
      articles = articles.filter((article) => {
        return (
          article.tags?.some((tag) => subsectionKeywords.some((keyword) => tag.toLowerCase().includes(keyword.toLowerCase()))) ||
          article.title.toLowerCase().includes(activeSubsection.value.toLowerCase())
        );
      });
    }

    // Filter by search query
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase();
      articles = articles.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.description.toLowerCase().includes(query) ||
          article.tags?.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    // Filter by selected tag
    if (selectedTag.value) {
      articles = articles.filter((article) => article.tags?.includes(selectedTag.value));
    }

    // Sort articles
    articles = [...articles].sort((a, b) => {
      switch (sortBy.value) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'trending':
          // Sort by trending status, then by date
          if (a.isTrending && !b.isTrending) return -1;
          if (!a.isTrending && b.isTrending) return 1;
          return new Date(b.date) - new Date(a.date);
        case 'date':
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

    return articles;
  });

  // Computed: check if any filters are active
  const hasActiveFilters = computed(() => {
    return searchQuery.value !== '' || selectedTag.value !== '';
  });

  // Function to clear all filters
  const clearAllFilters = () => {
    searchQuery.value = '';
    selectedTag.value = '';
  };

  // Function to scroll to top smoothly
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
    });
  };

  // Handle subsection navigation with scroll
  const handleSubsectionChange = (subsectionName) => {
    activeSubsection.value = subsectionName;
    scrollToTop();
  };

  // Handle tag click from article card
  const handleTagClick = (tag) => {
    selectedTag.value = tag;
  };
</script>

<style scoped>
  /* Tailwind CSS handles all styling */
</style>
